/**
 * DashboardPage.tsx — Reviewer / PM overview of all tech requests.
 *
 * Visible to: PodReviewer, ProductManager, Admin.
 *
 * Filtering strategy: fetches page_size=500 in a single request, then
 * filters client-side. This is intentional — the backend query params
 * only accept a single scalar value per field, so multi-select filters
 * (e.g. status=Submitted&status=InReview) cannot be expressed as URL
 * params without backend changes. Client-side filtering is the pragmatic
 * solution while 500 covers the realistic request volume.
 *
 * Stat cards use four separate single-item queries (page_size=1) just to
 * get accurate `total` counts per status — they don't need full item data.
 *
 * CSV export is generated client-side from the already-filtered array so
 * the download always matches exactly what the user sees on screen.
 */

import { useMemo, useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Activity,
  CheckCircle2,
  Clock,
  Download,
  FileText,
  Filter,
  Search,
  X,
  AlertTriangle,
  Bug,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { RequestTable } from "@/components/request/RequestTable";
import { useRequests, useEscalationSummary } from "@/hooks/useRequests";
import { useAuth } from "@/hooks/useAuth";
import { cn, ensureRegionArray } from "@/lib/utils";
import {
  PODS,
  PRIORITIES,
  REQUEST_STATUSES,
  STATUS_LABELS,
  STATUS_COLORS,
  STATUS_ACTIVE_COLORS,
  POD_COLORS,
  POD_ACTIVE_COLORS,
  POD_LABELS,
  PRIORITY_COLORS,
  PRIORITY_ACTIVE_COLORS,
  DASHBOARD_REFRESH_INTERVAL_MS as REFRESH_MS,
} from "@/lib/constants";
import type { Pod, RequestStatus, RequestType, Priority } from "@/lib/types";

// ── Constants ─────────────────────────────────────────────────────────────────

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Immutable toggle: adds `item` to `arr` if absent, removes it if present.
 * Used for all multi-select filter pill interactions.
 */
function toggle<T>(arr: T[], item: T): T[] {
  return arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];
}

// ── Sub-components ────────────────────────────────────────────────────────────

interface StatCardProps {
  icon: React.ElementType;
  label: string;
  value: number | undefined;
  color: string;
}

/** Single KPI tile shown in the top stat row. `value` is undefined while loading. */
function StatCard({ icon: Icon, label, value, color }: StatCardProps) {
  return (
    <Card>
      <CardContent className="flex items-center gap-4 pt-6 pb-5">
        <div
          className={cn(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg",
            color,
          )}
        >
          <Icon className="h-5 w-5 text-white" />
        </div>
        <div>
          <p className="text-2xl font-bold leading-none">{value ?? "—"}</p>
          <p className="text-xs text-muted-foreground mt-1">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Page component ────────────────────────────────────────────────────────────

/** Main reviewer/PM dashboard with multi-select filters and CSV export. */
export function DashboardPage() {
  const [searchParams] = useSearchParams();

  // ── Filter state ────────────────────────────────────────────────────────────
  // Initialize from URL params if present (drill-down from PM Summary)
  const [statuses, setStatuses] = useState<RequestStatus[]>(() => {
    const param = searchParams.get("status");
    return param ? [param as RequestStatus] : [];
  });
  const [pods, setPods] = useState<Pod[]>(() => {
    const param = searchParams.get("pod");
    return param ? [param as Pod] : [];
  });
  const [priorities, setPriorities] = useState<Priority[]>([]);
  const [typeView, setTypeView] = useState<RequestType | "all">("all");
  const [search, setSearch] = useState("");
  const [exporting, setExporting] = useState(false);

  // Filter panel state
  type FilterCat = "status" | "area" | "priority";
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState<FilterCat>("status");
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!filterOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        !panelRef.current?.contains(e.target as Node) &&
        !triggerRef.current?.contains(e.target as Node)
      ) {
        setFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [filterOpen]);

  const openPanel = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPanelPos({ top: rect.bottom + 6, left: rect.left });
    }
    setFilterOpen(true);
  };

  const togglePanel = () => (filterOpen ? setFilterOpen(false) : openPanel());

  const CAT_META: Record<
    FilterCat,
    {
      label: string;
      dot: string;
      line: string;
      badge: string;
      chip: string;
      catLabel: string;
    }
  > = {
    status: {
      label: "Status",
      catLabel: "Request Status",
      dot: "bg-blue-500",
      line: "bg-blue-500",
      badge: "bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300",
      chip: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700",
    },
    area: {
      label: "Area",
      catLabel: "Area of Expertise",
      dot: "bg-emerald-500",
      line: "bg-emerald-500",
      badge:
        "bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
      chip: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-700",
    },
    priority: {
      label: "Priority",
      catLabel: "Priority Level",
      dot: "bg-amber-500",
      line: "bg-amber-500",
      badge: "bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
      chip: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700",
    },
  };

  function getCatCount(cat: FilterCat) {
    return { status: statuses, area: pods, priority: priorities }[cat].length;
  }

  function clearFiltersOnly() {
    setStatuses([]);
    setPods([]);
    setPriorities([]);
  }

  function toggleOption(cat: FilterCat, value: string) {
    switch (cat) {
      case "status":
        setStatuses((prev) => toggle(prev, value as RequestStatus));
        break;
      case "area":
        setPods((prev) => toggle(prev, value as Pod));
        break;
      case "priority":
        setPriorities((prev) => toggle(prev, value as Priority));
        break;
    }
  }

  function isOptionSelected(cat: FilterCat, value: string): boolean {
    switch (cat) {
      case "status":
        return statuses.includes(value as RequestStatus);
      case "area":
        return pods.includes(value as Pod);
      case "priority":
        return priorities.includes(value as Priority);
    }
  }

  function getActiveClass(cat: FilterCat, value: string): string {
    switch (cat) {
      case "status":
        return STATUS_ACTIVE_COLORS[value as RequestStatus];
      case "area":
        return POD_ACTIVE_COLORS[value as Pod];
      case "priority":
        return PRIORITY_ACTIVE_COLORS[value as Priority];
    }
  }

  function getOptionLabel(cat: FilterCat, value: string): string {
    if (cat === "status") return STATUS_LABELS[value as RequestStatus];
    if (cat === "area") return POD_LABELS[value as Pod];
    return value;
  }

  function getOptions(cat: FilterCat): string[] {
    switch (cat) {
      case "status":
        return REQUEST_STATUSES as unknown as string[];
      case "area":
        return PODS as unknown as string[];
      case "priority":
        return PRIORITIES as unknown as string[];
    }
  }

  // Chip entries for the active-filter strip
  const chipEntries: Array<{ cat: FilterCat; value: string }> = [
    ...statuses.map((v) => ({ cat: "status" as FilterCat, value: v })),
    ...pods.map((v) => ({ cat: "area" as FilterCat, value: v })),
    ...priorities.map((v) => ({ cat: "priority" as FilterCat, value: v })),
  ];

  const { isPM, isReviewer } = useAuth();
  // Only PM and reviewers can export — requestors don't have access to this page
  const canExport = isPM || isReviewer;

  // ── Data fetching ───────────────────────────────────────────────────────────
  // page_size=500 fetches all requests so client-side multi-select filtering
  // works correctly. Search is the only filter sent to the server because it
  // can't be replicated client-side without the full text index.
  const { data, isLoading } = useRequests(
    { ...(search && { search }), page: 1, page_size: 500 },
    { refetchInterval: REFRESH_MS },
  );

  // Stat card counts use separate lightweight queries (page_size=1) — we only
  // need the `total` field from the response, not the full item list.
  const { data: totalData } = useRequests(
    { page_size: 1 },
    { refetchInterval: REFRESH_MS },
  );
  const { data: newData } = useRequests(
    { status: "Submitted", page_size: 1 },
    { refetchInterval: REFRESH_MS },
  );
  const { data: awaitData } = useRequests(
    { status: "AwaitingInfo", page_size: 1 },
    { refetchInterval: REFRESH_MS },
  );
  const { data: approvedData } = useRequests(
    { status: "Approved", page_size: 1 },
    { refetchInterval: REFRESH_MS },
  );

  // Escalation metrics: requests in AwaitingInfo >7 days
  const { data: escalationData } = useEscalationSummary();

  const allItems = data?.items ?? [];
  const hasEscalations = escalationData?.total
    ? escalationData.total > 0
    : false;

  // ── Client-side filtering ───────────────────────────────────────────────────
  // Applied on top of the server-fetched page. Multi-select arrays are ANDed:
  // a request must match ALL active filter groups, each group OR'd internally.
  // Type is applied last so the segmented control can show per-type counts
  // that respect every other active filter.
  const filteredExceptType = useMemo(() => {
    let items = allItems;
    if (statuses.length > 0)
      items = items.filter((r) => statuses.includes(r.status));
    if (pods.length > 0) items = items.filter((r) => pods.includes(r.pod));
    if (priorities.length > 0)
      items = items.filter((r) => priorities.includes(r.priority));
    return items;
  }, [allItems, statuses, pods, priorities]);

  const filtered = useMemo(
    () =>
      typeView === "all"
        ? filteredExceptType
        : filteredExceptType.filter((r) => r.request_type === typeView),
    [filteredExceptType, typeView],
  );

  const typeCounts = useMemo(
    () => ({
      all: filteredExceptType.length,
      Feature: filteredExceptType.filter((r) => r.request_type === "Feature")
        .length,
      Defect: filteredExceptType.filter((r) => r.request_type === "Defect")
        .length,
    }),
    [filteredExceptType],
  );

  const filterCount = statuses.length + pods.length + priorities.length;
  const activeCount =
    filterCount + (typeView !== "all" ? 1 : 0) + (search ? 1 : 0);
  const hasFilters = activeCount > 0;

  const clearAll = () => {
    setStatuses([]);
    setPods([]);
    setPriorities([]);
    setTypeView("all");
    setSearch("");
  };

  // ── CSV export ──────────────────────────────────────────────────────────────
  /**
   * Generates the CSV blob client-side from `filtered` (not from the API
   * export endpoint) so that active multi-select filters are correctly
   * reflected in the download. region is stored as string[] since migration
   * 007, so we join the array before writing to CSV.
   */
  const exportCsv = async () => {
    setExporting(true);
    try {
      const rows: string[][] = [
        [
          "Reference ID",
          "Title",
          "Type",
          "Status",
          "Area",
          "Priority",
          "Region",
          "Submitter Name",
          "Submitter Email",
          "Affected Area",
          "Claimed By",
          "Jira Ticket",
          "JSM Ticket",
          "Created At",
        ],
      ];
      for (const r of filtered) {
        rows.push([
          r.reference_id ?? "",
          r.title,
          r.request_type,
          STATUS_LABELS[r.status],
          POD_LABELS[r.pod],
          r.priority,
          // region is string[] post-migration 007; guard against legacy string values
          ensureRegionArray(r.region).join(", "),
          r.submitter_name,
          r.submitter_email,
          r.affected_area,
          r.claimed_by_email ?? "",
          r.jira_ticket_key ?? "",
          r.jsm_ticket_key ?? "",
          new Date(r.created_at).toISOString().slice(0, 16).replace("T", " ") +
            " UTC",
        ]);
      }
      // RFC 4180: quote every field and escape internal quotes by doubling them
      const csv = rows
        .map((row) =>
          row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","),
        )
        .join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `blink-requests-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Dashboard</h1>
          <p className="text-sm text-muted-foreground">
            Tech requests overview · refreshes every 30 s
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {hasFilters && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 text-xs h-8"
              onClick={clearAll}
            >
              <X className="h-3 w-3" />
              Clear {activeCount} filter{activeCount !== 1 ? "s" : ""}
            </Button>
          )}
          {canExport && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 text-xs h-8"
              onClick={exportCsv}
              disabled={exporting || filtered.length === 0}
            >
              <Download className="h-3 w-3" />
              {exporting
                ? "Exporting…"
                : `Export CSV${filtered.length > 0 ? ` (${filtered.length})` : ""}`}
            </Button>
          )}
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          icon={FileText}
          label="Total"
          value={totalData?.total}
          color="bg-primary"
        />
        <StatCard
          icon={Activity}
          label="New / Submitted"
          value={newData?.total}
          color="bg-amber-500"
        />
        <StatCard
          icon={Clock}
          label="Awaiting Info"
          value={awaitData?.total}
          color="bg-orange-500"
        />
        <StatCard
          icon={CheckCircle2}
          label="Approved"
          value={approvedData?.total}
          color="bg-green-600"
        />
      </div>

      {/* Escalation Alert */}
      {hasEscalations && escalationData && (
        <Card className="border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-red-900 dark:text-red-200">
                  ⚠️ Escalation Alert
                </h3>
                <p className="text-sm text-red-800 dark:text-red-300 mt-1">
                  <strong>{escalationData.total}</strong> request
                  {escalationData.total !== 1 ? "s" : ""} waiting for requestor
                  response for &gt;7 days
                  {escalationData.oldest_days && (
                    <>
                      . Oldest has been waiting{" "}
                      <strong>{escalationData.oldest_days} days</strong>
                    </>
                  )}
                </p>
                {Object.keys(escalationData.by_priority || {}).length > 0 && (
                  <div className="mt-2 text-xs text-red-700">
                    <span className="font-medium">By Priority:</span>{" "}
                    {Object.entries(escalationData.by_priority)
                      .map(([p, c]) => `${p}: ${c}`)
                      .join(" • ")}
                  </div>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3 border-red-300 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900 text-red-900 dark:text-red-200"
                  onClick={() => setStatuses(["AwaitingInfo"])}
                >
                  View Escalated Requests
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search + filter bar */}
      <div className="space-y-2">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="Search requests by title…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-9 h-10"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Type switch + filter trigger + active chips */}
        <div className="flex items-center gap-2 flex-wrap">
          <div
            role="radiogroup"
            aria-label="Request type"
            className="inline-flex shrink-0 rounded-md border bg-muted/50 p-0.5"
          >
            {(
              [
                { value: "all", label: "All", icon: null },
                { value: "Feature", label: "Features", icon: Sparkles },
                { value: "Defect", label: "Defects", icon: Bug },
              ] as const
            ).map(({ value, label, icon: Icon }) => {
              const active = typeView === value;
              return (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setTypeView(value)}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded px-3 py-1 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    active
                      ? "bg-card text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {Icon && <Icon className="h-3.5 w-3.5" />}
                  {label}
                  <span
                    className={cn(
                      "rounded-full px-1.5 text-[11px] tabular-nums",
                      active
                        ? "bg-primary/10 text-primary"
                        : "bg-muted text-muted-foreground",
                    )}
                  >
                    {typeCounts[value]}
                  </span>
                </button>
              );
            })}
          </div>

          <button
            ref={triggerRef}
            type="button"
            onClick={togglePanel}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium transition-all shrink-0",
              filterOpen
                ? "border-primary bg-primary/5 text-primary ring-2 ring-primary/10"
                : "border-border bg-card text-muted-foreground hover:border-muted-foreground hover:text-foreground",
            )}
          >
            <Filter className="h-3.5 w-3.5" />
            Filters
            {filterCount > 0 && (
              <span className="ml-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-white">
                {filterCount}
              </span>
            )}
          </button>

          {/* Active filter chips */}
          {chipEntries.map(({ cat, value }) => {
            const meta = CAT_META[cat];
            return (
              <div
                key={`${cat}-${value}`}
                className={cn(
                  "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium",
                  meta.chip,
                )}
              >
                <span className="text-[9px] uppercase tracking-wider opacity-60">
                  {meta.label}
                </span>
                <span className="mx-0.5">{getOptionLabel(cat, value)}</span>
                <button
                  type="button"
                  onClick={() => toggleOption(cat, value)}
                  className="ml-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full opacity-50 hover:opacity-100 hover:bg-black/10 dark:hover:bg-white/20 transition-opacity"
                  aria-label={`Remove ${getOptionLabel(cat, value)}`}
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </div>
            );
          })}

          {filterCount > 0 && (
            <button
              type="button"
              onClick={clearFiltersOnly}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border bg-card">
        {hasFilters && !isLoading && (
          <div className="flex items-center justify-between px-4 pt-3 pb-1">
            <p className="text-xs text-muted-foreground">
              Showing{" "}
              <span className="font-semibold text-foreground">
                {filtered.length}
              </span>{" "}
              of {allItems.length} requests
            </p>
          </div>
        )}
        <RequestTable requests={filtered} isLoading={isLoading} />
      </div>

      {/* Floating two-panel filter card */}
      {filterOpen && (
        <div
          ref={panelRef}
          className="fixed z-50 w-[530px] max-w-[calc(100vw-32px)] rounded-xl border bg-card shadow-xl"
          style={{ top: panelPos.top, left: panelPos.left }}
        >
          <div className="flex min-h-[228px]">
            {/* Left: category list */}
            <div className="w-36 shrink-0 border-r bg-muted/40 py-2">
              {(["status", "area", "priority"] as FilterCat[]).map((cat) => {
                const meta = CAT_META[cat];
                const count = getCatCount(cat);
                const isActive = activeCategory === cat;
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setActiveCategory(cat)}
                    className={cn(
                      "relative flex w-full items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium transition-colors text-left",
                      isActive
                        ? "bg-card text-foreground"
                        : "text-muted-foreground hover:bg-card/60 hover:text-foreground",
                    )}
                  >
                    {isActive && (
                      <span
                        className={cn(
                          "absolute left-0 top-2 bottom-2 w-0.5 rounded-r",
                          meta.line,
                        )}
                      />
                    )}
                    <span
                      className={cn(
                        "h-1.5 w-1.5 shrink-0 rounded-full",
                        meta.dot,
                        isActive ? "opacity-100" : "opacity-35",
                      )}
                    />
                    {meta.label}
                    {count > 0 && (
                      <span
                        className={cn(
                          "ml-auto rounded-full px-1.5 py-px text-[10px] font-bold",
                          meta.badge,
                        )}
                      >
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Right: option grid */}
            <div className="flex-1 p-4">
              <p className="mb-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                {CAT_META[activeCategory].catLabel}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {getOptions(activeCategory).map((value) => {
                  const selected = isOptionSelected(activeCategory, value);
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => toggleOption(activeCategory, value)}
                      aria-pressed={selected}
                      className={cn(
                        "rounded-full border px-3 py-1 text-xs font-medium transition-all",
                        selected
                          ? getActiveClass(activeCategory, value)
                          : "border-border bg-background text-foreground hover:border-muted-foreground hover:bg-muted/50",
                      )}
                    >
                      {getOptionLabel(activeCategory, value)}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between border-t px-4 py-2.5">
            <button
              type="button"
              onClick={clearFiltersOnly}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Clear all
            </button>
            <span className="text-[11px] text-muted-foreground/50">
              Filters apply instantly
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
